import { browser } from "$app/environment"
import {
  getCookieAuthOverride,
  isCookieAuthEnabled,
  isCookieModeRequested,
  setCookieAuthEnabled,
} from "$lib/cookie-auth"
import { FeatureFlagClient } from "$lib/feature-flags"
import { twApiClient } from "$lib/tastyworks-rest"
import session from "$lib/tastyworks-rest/session"
import { captureUtmParams } from "$lib/utm/utm-capture"
import {
  AO_AUTH_SESSION_COOKIE_KEY,
  type TwUser,
} from "@tastyworks/tastyworks-api"
import { CROSS_DOMAIN_COOKIE_DESER } from "@tastyworks/tastyworks-api/session"
import Cookie from "js-cookie"
import { isNil } from "lodash-es"
import { derived, get } from "svelte/store"
import { locale, waitLocale } from "svelte-i18n"
import { startTelemetry } from "$lib/telemetry"
import { setAmplitudeUserId } from "@tastyworks/shared-amplitude-analytics/amplitude"
import "$lib/i18n" // Import to initialize. Important
import "$lib/result" // Import to register the error reporter. Important
import {
  getIsIosAttAuthorized,
  getIsMobile,
  getWebViewPlatform,
} from "$lib/util"
import * as Sentry from "@sentry/sveltekit"
import { tcRestData } from "@tastyworks/error-handling"

export const load = async () => {
  console.log(`account opening v${__APP_VERSION__}`)
  if (browser) {
    locale.set(window.navigator.language)
  }
  await waitLocale()

  captureUtmParams()

  setSessionTokenFromCrossDomainCookie()

  const webViewPlatform = getWebViewPlatform()
  const isTastyWebView = derived(webViewPlatform, (platform) => !!platform)
  const isIosAttAuthorized = getIsIosAttAuthorized(get(webViewPlatform))

  if (browser) {
    resolveAuthMode(get(isTastyWebView))
  }
  const user = browser ? await validateSession() : null

  if (user) {
    session.user = user
    session.currentEmail = user.email
    session.externalId = user.externalId
    session.username = user.username
    lockAuthMode()
    Sentry.setUser({ id: user.externalId })
    setAmplitudeUserId(user.externalId)
  } else {
    Sentry.setUser(null)
    setAmplitudeUserId(null)
  }

  const client = await resolveFlagClient(user)

  startTelemetry()

  return {
    user,
    client,
    featureFlags: client.flags,
    isTastyWebView,
    webViewPlatform,
    isIosAttAuthorized,
  }
}

export const ssr = false
export const prerender = false

/**
 * Selects the auth transport for this load and sets it before the first
 * validate call — cookie mode has no bearer token to gate that call on.
 *
 * A session established earlier this browser session pins its transport via
 * `session.authMode` (see lockAuthMode), and a present bearer token means
 * definite bearer mode; both keep a user on the mode they started with.
 *
 * A webview host takes bearer: it hands the session token to native code at
 * completion, and in cookie mode there is no readable token to hand.
 * `auth-mode=cookie` releases that pin for hosts that read the cookie
 * themselves.
 */
function resolveAuthMode(isTastyWebView: boolean): void {
  if (session.authMode) {
    setCookieAuthEnabled(session.authMode === "cookie")
    return
  }

  if (session.sessionToken) {
    setCookieAuthEnabled(false)
    return
  }

  if (isTastyWebView && !isCookieModeRequested()) {
    setCookieAuthEnabled(false)
    return
  }

  setCookieAuthEnabled(getCookieAuthOverride() ?? true)
}

/**
 * Pins the resolved transport to the session on the first successful validate,
 * so every later load this browser session keeps the same auth mode. Only the
 * first established session sets it.
 */
function lockAuthMode() {
  if (!session.authMode) {
    session.authMode = isCookieAuthEnabled() ? "cookie" : "bearer"
  }
}

async function resolveFlagClient(
  user: TwUser | null
): Promise<FeatureFlagClient> {
  const client = user
    ? new FeatureFlagClient({
        isEmployeeDomain: session.isEmployeeDomain,
        isNewUser: session.isNewUser,
        isMobile: getIsMobile(),
      })
    : new FeatureFlagClient()
  await client.initialize()
  return client
}

/**
 * Resolves the current user from the session.
 *
 * In cookie mode the HttpOnly session cookie is the source of truth, so we
 * validate unconditionally (no stored token to gate on) and let the cookie-csrf
 * transport carry the credentials. The placeholder token only satisfies the
 * service's non-empty guard. In bearer mode we keep validating the stored token.
 */
async function validateSession() {
  if (isCookieAuthEnabled()) {
    const userResult = await tcRestData(
      () => twApiClient.sessionService.validate("cookie-auth"),
      { resource: "User", operation: "validate" }
    )

    return userResult.ok ? userResult.value : null
  }

  if (!session.sessionToken) {
    return null
  }

  const response = await twApiClient.sessionService.validate(
    session.sessionToken
  )
  // 401 returns null data but no error
  if (response.isError || !response.hasData()) {
    session.clear()
  }
  return response.data
}

function setSessionTokenFromCrossDomainCookie() {
  const authCookie = Cookie.get(AO_AUTH_SESSION_COOKIE_KEY)

  if (!isNil(authCookie)) {
    const payload = CROSS_DOMAIN_COOKIE_DESER.parse(authCookie)
    session.sessionToken = payload.authenticated.data.sessionToken
    Cookie.remove(AO_AUTH_SESSION_COOKIE_KEY, {
      domain: import.meta.env.VITE_SESSION_DOMAIN,
    })
  }
}
